/**
 * "המחירון שלי" — supplier default price list + customer price lists.
 * Route: /price-list (suppliers only).
 */
import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CustomerLayout from "../../layouts/customer";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import {
  discardDraft,
  fillMissingColumn,
  removeDiameter,
  setDiameterPrice,
  setDraftField,
} from "Redux/features/supplierPricing";
import { fetchSupplierPricing, saveSupplierPricing } from "Redux/middlwares/supplierPricing";
import { PriceList } from "../../utils/supplier-pricing-engine";
import { PBtn, PCard, PField, PNumber, PPill, PSelect, PTabs, PToggle, downloadCsv, fmtDate, toNum } from "./ui";
import PriceHistoryModal from "./price-history-modal";
import CustomerPriceLists from "./customer-price-lists";
import CustomerPricingModal from "./customer-pricing-modal";
import MobileDiameterPrices from "./mobile-diameter-prices";

const COLUMNS: ("ribbed" | "smooth" | "weldableExtra")[] = ["ribbed", "smooth", "weldableExtra"];

export const exportPriceListCsv = (pl: PriceList) => {
  const rows: (string | number | null)[][] = [["קוטר (מ״מ)", "מצולע ₪/טון", "עגול ₪/טון", "תוספת רתיך ₪/טון"]];
  pl.diameterPrices.forEach((r) => rows.push([r.diameter, r.ribbed, r.smooth, r.weldableExtra]));
  rows.push([]);
  rows.push(["כיפוף ₪/טון", pl.bendPerTon], ["חיתוך ₪/טון", pl.cutPerTon], ["יחידה ₪", pl.unitPrice], ["מינימום ליחידה ₪", pl.minUnitPrice]);
  rows.push(["הובלה", pl.transportMethod, pl.transportPrice], ["מע״מ %", pl.vatPercent], ["פחת %", pl.wastePercent]);
  downloadCsv("easybar-price-list.csv", rows);
};

function SupplierPricingPage() {
  const { t } = useTranslation(["supplierPricing"]);
  const dispatch = useAppDispatch();
  const { status, draft, dirty, customLists } = useAppSelector((state) => state.supplierPricing);
  const rightToLeft = useAppSelector((state) => state.root.rightToLeft);
  const [tab, setTab] = useState("default");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [customerId, setCustomerId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchSupplierPricing());
  }, [dispatch]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const setField = (key: keyof PriceList) => (value: string) => dispatch(setDraftField({ key, value: toNum(value) }));

  return (
    <CustomerLayout>
      <div className="eb-pr eb-pr-page eb-pr-page--layout" dir={rightToLeft ? "rtl" : "ltr"}>
        {status === "error" && <div className="eb-pr-warn">{t("common.error")}</div>}
        {!draft ? (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <header className="eb-pr-between">
              <div>
                <h1 className="eb-pr-h1">{t("title")}</h1>
                <p className="eb-pr-sub">
                  {t("subtitle")}{" "}
                  {draft.lastModified && t("lastUpdated", { date: fmtDate(draft.lastModified), user: draft.updatedByName || "" })}
                </p>
              </div>
              <div className="eb-pr-row">
                {dirty && <PPill tone="amber">{t("unsaved")}</PPill>}
                <PBtn kind="ghost" onClick={() => setHistoryOpen(true)}>
                  {t("history")}
                </PBtn>
                <PBtn kind="secondary" onClick={() => exportPriceListCsv(draft)}>
                  {t("exportExcel")}
                </PBtn>
                {dirty && (
                  <PBtn kind="secondary" onClick={() => dispatch(discardDraft())}>
                    {t("discard")}
                  </PBtn>
                )}
                <PBtn disabled={!dirty || status === "saving"} onClick={() => dispatch(saveSupplierPricing())}>
                  {status === "saving" ? t("saving") : t("save")}
                </PBtn>
              </div>
            </header>

            <PTabs
              value={tab}
              onChange={setTab}
              tabs={[
                { value: "default", label: t("tabs.default") },
                { value: "customers", label: t("tabs.customers", { count: customLists.length }) },
              ]}
            />

            {tab === "customers" ? (
              <CustomerPriceLists onEdit={setCustomerId} />
            ) : (
              <>
                <div className="eb-pr-grid">
                  <PCard
                    title={t("diameter.title")}
                    subtitle={t("diameter.subtitle")}
                  >
                    <MobileDiameterPrices
                      rows={draft.diameterPrices}
                      onFillMissing={(column) => dispatch(fillMissingColumn(column))}
                      onChange={(diameter, column, value) => dispatch(setDiameterPrice({ diameter, column, value }))}
                      onRemove={(diameter) => dispatch(removeDiameter(diameter))}
                    />
                    <table className="eb-pr-table eb-pr-diameter-desktop">
                      <thead>
                        <tr>
                          <th>{t("diameter.col")}</th>
                          {COLUMNS.map((c) => (
                            <th key={c}>
                              <div className="eb-pr-between">
                                <span>{t(`diameter.${c}`)}</span>
                                <button type="button" className="eb-pr-link" onClick={() => dispatch(fillMissingColumn(c))}>
                                  {t("diameter.fillMissing")}
                                </button>
                              </div>
                            </th>
                          ))}
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {draft.diameterPrices.map((r) => (
                          <tr key={r.diameter}>
                            <td>
                              <b>{r.diameter}</b>
                            </td>
                            {COLUMNS.map((c) => (
                              <td key={c}>
                                <PNumber
                                  size="sm"
                                  ariaLabel={`${r.diameter} ${c}`}
                                  value={r[c]}
                                  warn={c === "ribbed" && r[c] === null}
                                  placeholder={c === "ribbed" ? t("diameter.missing") : "—"}
                                  onChange={(value) => dispatch(setDiameterPrice({ diameter: r.diameter, column: c, value }))}
                                />
                              </td>
                            ))}
                            <td>
                              <button type="button" className="eb-pr-link" style={{ color: "#c0392b" }} onClick={() => dispatch(removeDiameter(r.diameter))}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                        {draft.diameterPrices.length === 0 && (
                          <tr>
                            <td colSpan={5} className="eb-pr-muted">
                              {t("diameter.subtitle")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </PCard>

                  <div className="eb-pr-col">
                    <PCard title={t("processing.title")} subtitle={t("processing.subtitle")}>
                      <div className="eb-pr-row">
                        <PField label={t("processing.bend")} style={{ flex: 1 }}>
                          <PNumber value={draft.bendPerTon} onChange={setField("bendPerTon")} suffix={t("units.perTon")} />
                        </PField>
                        <PField label={t("processing.cut")} style={{ flex: 1 }}>
                          <PNumber value={draft.cutPerTon} onChange={setField("cutPerTon")} suffix={t("units.perTon")} />
                        </PField>
                      </div>
                      <div className="eb-pr-row">
                        <PField label={t("processing.unitPrice")} style={{ flex: 1 }}>
                          <PNumber value={draft.unitPrice} onChange={setField("unitPrice")} suffix={t("units.perUnit")} />
                        </PField>
                        <PField label={t("processing.minUnit")} style={{ flex: 1 }}>
                          <PNumber value={draft.minUnitPrice} onChange={setField("minUnitPrice")} suffix={t("units.ils")} />
                        </PField>
                      </div>
                      <div className="eb-pr-rule">
                        <PToggle
                          on={draft.shortShapeAsUnit}
                          label={t("processing.shortRulePrefix")}
                          onChange={(v) => dispatch(setDraftField({ key: "shortShapeAsUnit", value: v }))}
                        />
                        <span>{t("processing.shortRulePrefix")}</span>
                        <PNumber size="sm" value={draft.shortShapeMaxCm} onChange={setField("shortShapeMaxCm")} ariaLabel="cm" />
                        <span>{t("processing.shortRuleSuffix")}</span>
                      </div>
                    </PCard>

                    <PCard title={t("transport.title")} subtitle={t("transport.subtitle")}>
                      <div className="eb-pr-row">
                        <PField label={t("transport.method")} style={{ flex: 1 }}>
                          <PSelect
                            value={draft.transportMethod}
                            onChange={(v) => dispatch(setDraftField({ key: "transportMethod", value: v }))}
                            options={["per_ton", "fixed", "none"].map((m) => ({ value: m, label: t(`transport.methods.${m}`) }))}
                          />
                        </PField>
                        <PField label={t("transport.price")} style={{ flex: 1 }}>
                          <PNumber
                            value={draft.transportPrice}
                            onChange={setField("transportPrice")}
                            disabled={draft.transportMethod === "none"}
                            suffix={draft.transportMethod === "per_ton" ? t("units.perTon") : t("units.ils")}
                          />
                        </PField>
                      </div>
                      <div className="eb-pr-row">
                        <PField label={t("transport.freeAbove")} style={{ flex: 1 }}>
                          <PNumber value={draft.transportFreeAboveTons} onChange={setField("transportFreeAboveTons")} suffix={t("units.ton")} />
                        </PField>
                        <PField label={t("transport.minimum")} style={{ flex: 1 }}>
                          <PNumber value={draft.transportMinimum} onChange={setField("transportMinimum")} suffix={t("units.ils")} />
                        </PField>
                      </div>
                    </PCard>

                    <PCard title={t("general.title")}>
                      <div className="eb-pr-row">
                        <PField label={t("general.waste")} style={{ flex: 1 }}>
                          <PNumber value={draft.wastePercent} onChange={setField("wastePercent")} suffix={t("units.percent")} />
                        </PField>
                        <PField label={t("general.vat")} style={{ flex: 1 }}>
                          <PNumber value={draft.vatPercent} onChange={setField("vatPercent")} suffix={t("units.percent")} />
                        </PField>
                      </div>
                      <PField label={t("general.validity")}>
                        <PSelect
                          value={draft.quoteValidityDays}
                          onChange={(v) => dispatch(setDraftField({ key: "quoteValidityDays", value: Number(v) }))}
                          options={[3, 7, 14, 30].map((d) => ({ value: d, label: t("general.days", { count: d }) }))}
                        />
                      </PField>
                    </PCard>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <PriceHistoryModal show={historyOpen} onHide={() => setHistoryOpen(false)} />
      <CustomerPricingModal customerId={customerId} onHide={() => setCustomerId(null)} />
    </CustomerLayout>
  );
}

export default SupplierPricingPage;
